import { createFileRoute } from "@tanstack/react-router";
import {
  LiveChart,
  LiveRatesTable,
  HindiWeightCalc,
  InHiLinkMatrix,
  FaqAccordion,
  CityRatesGrid,
  buildInHiSchema,
  HindiUniqueBlock,
  hiDateParts,
  goldRatesQueryOptions,
} from "@/components/in-hi/shell";

const SLUG = "/in/hi/gold-silver-city-rates";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "कानपुर में सोना चांदी का भाव आज क्या चल रहा है?", a: "कानपुर के सराफा एसोसिएशन का आज का 22K और 24K सोने का भाव ऊपर दी गयी 'शहर-अनुसार' ग्रिड में लाइव अपडेट कर दिया गया है। स्थानीय ज्वेलर्स की मेकिंग चार्ज अलग से जुड़ती है।" },
  { q: "अहमदाबाद में आज चांदी का रेट प्रति किलो कितना है?", a: "अहमदाबाद में आज चांदी का प्रति किलो भाव अंतरराष्ट्रीय स्पॉट × USD/INR से निकाला जाता है और छोटे स्थानीय मार्जिन के साथ ग्रिड में दिखता है। सटीक ज्वेलर बिल में हॉलमार्किंग और GST जुड़ती है।" },
  { q: "सोना चांदी का रेट हरियाणा और राजस्थान के बाजारों में क्यों बदलता है?", a: "हरियाणा (गुरुग्राम, फरीदाबाद) और राजस्थान (जयपुर, जोधपुर) के दामों में मामूली अंतर मुख्यतः तीन कारणों से आता है: राज्य GST/स्टेट-लेवी नहीं (यह समान है) लेकिन ट्रांसपोर्टेशन, स्थानीय सराफा एसोसिएशन के दैनिक क्लोज़िंग रेट और आभूषण की मांग/आपूर्ति में फर्क।" },
];

export const Route = createFileRoute("/in/hi/gold-silver-city-rates")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = hiDateParts();
    const title = `अहमदाबाद, कानपुर, हरियाणा, राजस्थान में आज सोना चांदी का रेट — लाइव भाव`;
    const description = `भारत के प्रमुख शहरों — अहमदाबाद, कानपुर, हरियाणा, राजस्थान, दिल्ली, मुंबई — में आज का 22K/24K सोने और चांदी का लाइव भाव प्रति 10 ग्राम व प्रति किलो।`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "hi-IN" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "hi_IN" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "hi-IN", href: CANONICAL },
      ],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(buildInHiSchema({ slug: SLUG, name: title, description, faqs: FAQS, breadcrumbLabel: "शहर-अनुसार सोना-चांदी भाव" })) }],
    };
  },
  component: Page,
});

function Page() {
  const dp = hiDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">भारत · शहर-अनुसार</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          अहमदाबाद, कानपुर, हरियाणा और राजस्थान में आज सोने चांदी का रेट ({dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          <strong>भारत के प्रमुख शहरों और राज्यों</strong> में आज सोने-चांदी का सराफा रेट यहां लाइव अपडेट होता है। स्थानीय सराफा एसोसिएशन, ट्रांसपोर्ट कॉस्ट और ज्वेलर मार्जिन के हिसाब से हर शहर का दाम थोड़ा अलग होता है।
        </p>
      </header>
      <HindiUniqueBlock slug={SLUG} />
      <LiveChart title={`भारतीय शहरों का सोना ट्रेंड — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <CityRatesGrid />
      <section className="mx-auto max-w-6xl px-4 py-4 space-y-4">
        <div>
          <h2 className="text-lg md:text-xl font-bold">अहमदाबाद में आज का सोने का भाव</h2>
          <p className="text-sm text-muted-foreground mt-1">अहमदाबाद गुजरात का प्रमुख बुलियन केंद्र है। यहां का 22K/24K भाव ऊपर की ग्रिड में लाइव है; स्थानीय ज्वेलर्स 200–450 प्रति ग्राम की मेकिंग जोड़ते हैं।</p>
        </div>
        <div>
          <h3 className="text-base md:text-lg font-bold">कानपुर में सोना चांदी का भाव</h3>
          <p className="text-sm text-muted-foreground mt-1">कानपुर सर्राफा एसोसिएशन UP के भावों का बेंचमार्क माना जाता है। ऊपर ग्रिड में कानपुर का 22K/24K लाइव भाव दिखाया गया है।</p>
        </div>
        <div>
          <h3 className="text-base md:text-lg font-bold">हरियाणा और राजस्थान में आज का सोने का रेट</h3>
          <p className="text-sm text-muted-foreground mt-1">हरियाणा (गुरुग्राम, फरीदाबाद) दिल्ली भाव के करीब चलता है; राजस्थान (जयपुर) अपने पारंपरिक कुंदन/जड़ाऊ कारोबार के कारण मामूली प्रीमियम रखता है।</p>
        </div>
      </section>
      <HindiWeightCalc />
      <FaqAccordion items={FAQS} />
      <InHiLinkMatrix current={SLUG} />
    </main>
  );
}